import React from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { motion, AnimatePresence } from 'framer-motion'

export type IButton = {
    label: string
    value: number | string
    icon?: React.ReactNode
    count?: number
    disabled?: boolean
}

interface ButtonGroupCustomizedProps {
    buttons: IButton[]
    active: string | number
    onChange: (value: string | number) => void
    className?: string
}

const ButtonGroupCustomized = ({
    buttons,
    active,
    onChange,
    className = '',
}: ButtonGroupCustomizedProps) => {
    const { t } = useTranslation()

    return (
        <div
            className={`flex items-center ${className} w-full gap-2 overflow-x-auto`}
        >
            {buttons.map((button) => {
                const isActive = button.value === active
                return (
                    <motion.button
                        key={button.value}
                        onClick={() => {
                            if (button.disabled) return
                            onChange(button.value)
                        }}
                        className={`
                            rounded-md py-auto cursor-pointer relative overflow-hidden h-[40px]
                            flex-1 min-w-fit
                            ${isActive ? 'text-primary' : 'text-gray-700 hover:text-gray-900'}
                            ${button.disabled ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                        whileHover={{
                            scale: 1.02,
                            transition: { duration: 0.2 },
                        }}
                        whileTap={{
                            scale: 0.98,
                            transition: { duration: 0.1 },
                        }}
                        animate={{
                            scale: isActive ? 1.02 : 1,
                            transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                    >
                        {/* Background animation */}
                        <AnimatePresence>
                            {isActive && (
                                <motion.div
                                    layoutId="activeBackground"
                                    className="absolute inset-0 bg-secondary-20 rounded-md"
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.8 }}
                                    transition={{
                                        duration: 0.3,
                                        ease: 'easeOut',
                                        layout: {
                                            duration: 0.4,
                                            ease: 'easeInOut',
                                        },
                                    }}
                                />
                            )}
                        </AnimatePresence>

                        {/* Hover background */}
                        <motion.div
                            className="absolute inset-0 bg-gray-100 rounded-md"
                            initial={{ opacity: 0 }}
                            whileHover={{
                                opacity: isActive ? 0 : 0.7,
                                transition: { duration: 0.2 },
                            }}
                        />

                        {/* Content */}
                        <motion.div
                            className="flex items-center justify-center gap-2 relative z-10"
                            animate={{
                                y: isActive ? -1 : 0,
                                transition: { duration: 0.2 },
                            }}
                        >
                            {button.icon && (
                                <motion.span
                                    className={`
                                        ${isActive ? 'text-primary' : 'text-gray-600'} w-4 h-4
                                    `}
                                    animate={{
                                        rotate: isActive ? [0, -5, 5, 0] : 0,
                                        transition: { duration: 0.4 },
                                    }}
                                >
                                    {button.icon}
                                </motion.span>
                            )}

                            <motion.div
                                animate={{
                                    fontWeight: isActive ? 600 : 500,
                                    transition: { duration: 0.2 },
                                }}
                            >
                                <Typogaphy
                                    className={`
                                    text-sm font-medium whitespace-nowrap
                                    ${isActive ? 'text-primary' : 'text-gray-500'}
                                `}
                                >
                                    {t(button.label) || button.label}
                                </Typogaphy>
                            </motion.div>

                            {button.count !== undefined && (
                                <motion.span
                                    className={`
                                        w-5 h-5 flex items-center justify-center rounded-md text-xs ml-1 font-medium
                                        ${
                                            isActive
                                                ? 'bg-[#D3E6FF] text-primary'
                                                : 'bg-gray-200 text-gray-600'
                                        }
                                    `}
                                    animate={{
                                        scale: isActive ? [1, 1.2, 1] : 1,
                                        transition: { duration: 0.3 },
                                    }}
                                    whileHover={{
                                        scale: 1.1,
                                        transition: { duration: 0.1 },
                                    }}
                                >
                                    {button.count}
                                </motion.span>
                            )}
                        </motion.div>
                    </motion.button>
                )
            })}
        </div>
    )
}

export default ButtonGroupCustomized
